import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import TrustSafety from './components/TrustSafety';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';
import ServiceModal from './components/ServiceModal';
import PricingSection from './components/PricingSection';
import LocationRedirect from '../../components/feature/LocationRedirect';
import { Link } from 'react-router-dom';

export type ServiceType = 'handyman' | 'plumbing' | 'electrical' | 'waste' | 'transportation' | 'cleaning';

export default function HomePage() {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToTop = () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.warn('Failed to scroll to top:', error);
    }
  };

  const handleServiceSelect = (service: ServiceType) => {
    try {
      setSelectedService(service);
    } catch (error) {
      console.error('Failed to select service:', error);
    }
  };

  const handleCloseModal = () => {
    try {
      setSelectedService(null);
    } catch (error) {
      console.error('Failed to close modal:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Location Redirect Component */}
      <LocationRedirect />
      
      {/* Hero Section with Booking Module - Same for Mobile and Desktop */}
      <Hero onServiceSelect={handleServiceSelect} />

      {/* Mobile View - Content after Hero */}
      <div className="lg:hidden">
        {/* Main Content Area */}
        <div className="bg-white pb-6">
          {/* NEW: Why TaskBudy Section - MOBILE */}
          <section className="py-8 px-4 mt-6">
            <div className="bg-gradient-to-br from-white via-orange-50/30 to-teal-50/20 rounded-3xl border border-gray-100 shadow-xl overflow-hidden relative">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-orange-200/40 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-teal-200/30 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute top-4 right-4 w-16 h-16 border-2 border-orange-200/40 rounded-xl rotate-12 opacity-50"></div>
              <div className="absolute bottom-4 left-4 w-12 h-12 border-2 border-teal-200/40 rounded-full opacity-50"></div>
              
              {/* Top accent bar */}
              <div className="h-1.5 w-full bg-gradient-to-r from-orange-500 via-amber-500 to-teal-500"></div>
              
              <div className="relative z-10 p-6">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-full text-xs font-bold mb-5 shadow-lg">
                  <i className="ri-sparkling-fill text-sm"></i>
                  <span>Why TaskBudy</span>
                </div>
                
                {/* Heading */}
                <h2 className="text-2xl font-extrabold text-[#22304e] mb-4 leading-tight">
                  Clear, upfront pricing
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500 mt-1">
                    No hidden fees, ever.
                  </span>
                </h2>
                
                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  TaskBudy uses a transparent pricing model so you always know the cost before booking. Most services are priced by time, starting with a minimum first hour and billed in simple half-hour increments after that.
                </p>
                
                {/* Feature list */}
                <div className="space-y-3 mb-6">
                  {[
                    { icon: 'ri-shield-check-fill', text: 'Verified & vetted professionals', color: 'from-orange-500 to-amber-500' },
                    { icon: 'ri-price-tag-3-fill', text: 'No quotes or negotiations needed', color: 'from-teal-500 to-emerald-500' },
                    { icon: 'ri-flashlight-fill', text: 'Same-day service available', color: 'from-rose-500 to-pink-500' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md flex-shrink-0`}>
                        <i className={`${item.icon} text-white text-sm`}></i>
                      </div>
                      <span className="text-sm font-semibold text-[#22304e]">{item.text}</span>
                    </div>
                  ))}
                </div>
                
                {/* Stats grid */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-md text-center relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-lg">
                        <i className="ri-map-pin-fill text-white text-base"></i>
                      </div>
                      <p className="text-lg font-black text-[#22304e]">12+</p>
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Cities</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-md text-center relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-lg">
                        <i className="ri-tools-fill text-white text-base"></i>
                      </div>
                      <p className="text-lg font-black text-[#22304e]">6</p>
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Trades</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-md text-center relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-lg">
                        <i className="ri-shield-check-fill text-white text-base"></i>
                      </div>
                      <p className="text-lg font-black text-[#22304e]">100%</p>
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Verified</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How It Works Section - MOBILE */}
          <section className="py-8 px-4 bg-gradient-to-br from-slate-50/80 via-white to-orange-50/30 relative overflow-hidden">
            {/* Subtle Background Elements */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-orange-100 rounded-full filter blur-3xl opacity-15 -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-teal-100 rounded-full filter blur-3xl opacity-15 translate-x-1/2 translate-y-1/2"></div>
            
            <div className="relative z-10">
              {/* Compact Container */}
              <div className="relative bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
                {/* Top Accent Bar */}
                <div className="h-1 bg-gradient-to-r from-orange-500 via-teal-500 to-orange-500"></div>

                <div className="p-4">
                  {/* Compact Header */}
                  <div className="text-center mb-4">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-orange-500/10 to-teal-500/10 border border-orange-200/50 rounded-full mb-2">
                      <i className="ri-flow-chart text-orange-500 text-sm"></i>
                      <span className="text-xs font-semibold text-orange-700 tracking-wide uppercase">Simple Process</span>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 mb-0.5">
                      How It Works
                    </h2>
                    <p className="text-xs text-gray-500">Book your service in four easy steps</p>
                  </div>

                  {/* Steps Grid - 2x2 on mobile */}
                  <div className="grid grid-cols-2 gap-2.5">
                    {[
                      { number: '1', icon: 'ri-map-pin-line', title: 'Enter Postcode', description: 'Check availability', color: 'orange', bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', ring: 'ring-orange-100', btnBg: 'bg-orange-500' },
                      { number: '2', icon: 'ri-tools-line', title: 'Select Service', description: 'Choose your trade', color: 'teal', bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200', ring: 'ring-teal-100', btnBg: 'bg-teal-500' },
                      { number: '3', icon: 'ri-secure-payment-line', title: 'Book & Pay', description: 'Secure payment', color: 'sky', bg: 'bg-sky-50', text: 'text-sky-600', border: 'border-sky-200', ring: 'ring-sky-100', btnBg: 'bg-sky-500' },
                      { number: '4', icon: 'ri-checkbox-circle-line', title: 'Job Complete', description: 'Track & rate', color: 'emerald', bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', ring: 'ring-emerald-100', btnBg: 'bg-emerald-500' }
                    ].map((step, index) => (
                      <div key={index} className="group relative">
                        <div className={`relative bg-gradient-to-br from-white to-gray-50/50 rounded-xl p-3 border ${step.border} hover:border-transparent hover:shadow-md transition-all duration-300 h-full`}>
                          {/* Step Number Badge */}
                          <div className={`absolute -top-1.5 -right-1.5 w-5 h-5 ${step.btnBg} rounded-md flex items-center justify-center shadow-md z-10`}>
                            <span className="text-[10px] font-bold text-white">{step.number}</span>
                          </div>

                          {/* Icon */}
                          <div className={`w-9 h-9 ${step.bg} rounded-lg flex items-center justify-center mb-2 mx-auto group-hover:scale-105 transition-transform duration-300 ring-2 ${step.ring}`}>
                            <i className={`${step.icon} text-base ${step.text}`}></i>
                          </div>

                          {/* Content */}
                          <div className="text-center">
                            <h3 className="text-xs font-semibold text-gray-900 mb-0.5 leading-tight">
                              {step.title}
                            </h3>
                            <p className="text-[10px] text-gray-500 leading-tight">
                              {step.description}
                            </p>
                          </div>

                          {/* Bottom Accent */}
                          <div className={`absolute bottom-0 left-1.5 right-1.5 h-0.5 ${step.btnBg} rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Bottom Info Bar */}
                  <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-2.5">
                    <div className="flex flex-wrap items-center justify-center gap-3 text-[10px] text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line text-orange-500 text-xs"></i>
                        Book in minutes
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-shield-check-line text-teal-500 text-xs"></i>
                        Verified pros
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-secure-payment-line text-emerald-500 text-xs"></i>
                        Secure payments
                      </span>
                    </div>
                    <button 
                      onClick={scrollToTop}
                      className="w-full flex items-center justify-center gap-1.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2.5 rounded-lg text-xs font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-sm hover:shadow-md whitespace-nowrap cursor-pointer"
                    >
                      Get Started
                      <i className="ri-arrow-up-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 3. Pricing Section - MOBILE */}
          <div className="py-8 px-4 bg-gradient-to-br from-slate-50 via-white to-orange-50/40 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute -top-16 -left-16 w-32 h-32 bg-gradient-to-br from-orange-200/40 to-amber-100/20 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-16 -right-16 w-32 h-32 bg-gradient-to-tl from-teal-200/30 to-cyan-100/20 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-200/60 rounded-full text-xs font-bold mb-2 shadow-lg shadow-orange-500/25">
                    <i className="ri-price-tag-3-fill text-xs"></i>
                    <span>Service Pricing</span>
                  </div>
                  <h2 className="text-lg font-bold text-[#22304e]">
                    Transparent Pricing
                  </h2>
                  <p className="text-xs text-gray-500">No hidden fees. Prices vary by location.</p>
                </div>
                <Link
                  to="/pricing"
                  className="text-orange-500 text-xs font-semibold flex items-center gap-1 whitespace-nowrap"
                >
                  View all
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>

              {/* Scrollable Cards */}
              <div className="overflow-x-auto hide-scrollbar -mx-4 px-4">
                <div className="flex gap-4 pb-4">
                  {[
                    {
                      key: 'handyman',
                      icon: 'ri-tools-fill',
                      title: 'Handyman',
                      subtitle: 'Repairs & Installations',
                      description: 'From furniture assembly to wall mounting and general repairs.',
                      bullets: ['Furniture assembly', 'TV & wall mounting', 'Door & lock repairs', 'Shelving & fixtures'],
                      priceMain: '£40–£55',
                      priceSuffix: '/hr',
                      priceNote: '1hr minimum',
                      image: 'https://public.readdy.ai/ai/img_res/2967642ef0c3e39a81f8366570280834.jpg',
                      accent: 'from-orange-500 to-amber-500',
                      accentLight: 'bg-orange-50',
                      iconBg: 'bg-gradient-to-br from-orange-500 to-amber-500',
                      borderColor: 'border-orange-200',
                      tagColor: 'bg-orange-100 text-orange-700',
                      tag: 'Most Popular'
                    },
                    {
                      key: 'plumbing',
                      icon: 'ri-drop-fill',
                      title: 'Plumbing',
                      subtitle: 'Repairs & Maintenance',
                      description: 'Leaking taps, blocked drains, or appliance installations.',
                      bullets: ['Leaking taps & pipes', 'Blocked drains', 'Toilet & cistern repairs', 'Appliance installation'],
                      priceMain: '£60–£80',
                      priceSuffix: '/hr',
                      priceNote: '1hr minimum',
                      image: 'https://readdy.ai/api/search-image?query=professional%20plumber%20in%20clean%20uniform%20repairing%20modern%20chrome%20kitchen%20tap%20with%20tools%20in%20bright%20contemporary%20home%20interior%20warm%20natural%20lighting%20clean%20simple%20background%20minimalist%20style&width=600&height=400&seq=pricing-plumbing-card-v3&orientation=landscape',
                      accent: 'from-sky-500 to-cyan-500',
                      accentLight: 'bg-sky-50',
                      iconBg: 'bg-gradient-to-br from-sky-500 to-cyan-500',
                      borderColor: 'border-sky-200',
                      tagColor: 'bg-sky-100 text-sky-700',
                      tag: 'Emergency Available'
                    },
                    {
                      key: 'electrical',
                      icon: 'ri-flashlight-fill',
                      title: 'Electrical',
                      subtitle: 'Minor Work & Checks',
                      description: 'Light replacements, socket installations, and safety checks.',
                      bullets: ['Light replacement', 'Socket installation', 'Fault finding', 'Safety inspections'],
                      priceMain: '£55–£75',
                      priceSuffix: '/hr',
                      priceNote: '1hr minimum',
                      image: 'https://readdy.ai/api/search-image?query=qualified%20electrician%20in%20clean%20uniform%20installing%20modern%20light%20fixture%20on%20ceiling%20in%20bright%20contemporary%20home%20interior%20warm%20natural%20lighting%20clean%20simple%20background%20minimalist%20style&width=600&height=400&seq=pricing-electrical-card-v3&orientation=landscape',
                      accent: 'from-amber-500 to-yellow-500',
                      accentLight: 'bg-amber-50',
                      iconBg: 'bg-gradient-to-br from-amber-500 to-yellow-500',
                      borderColor: 'border-amber-200',
                      tagColor: 'bg-amber-100 text-amber-700',
                      tag: 'Certified Pros'
                    },
                    {
                      key: 'cleaning',
                      icon: 'ri-brush-fill',
                      title: 'Cleaning',
                      subtitle: 'Domestic & Property',
                      description: 'Deep cleans, end-of-tenancy, and regular maintenance.',
                      bullets: ['Deep cleaning', 'End-of-tenancy', 'Kitchen & bathroom', 'Regular scheduled cleans'],
                      priceMain: '£30–£35',
                      priceSuffix: '/hr',
                      priceNote: '2hr minimum',
                      image: 'https://readdy.ai/api/search-image?query=professional%20cleaner%20in%20clean%20uniform%20deep%20cleaning%20modern%20bright%20kitchen%20with%20spray%20bottle%20and%20cloth%20in%20contemporary%20home%20interior%20warm%20natural%20lighting%20clean%20simple%20background%20minimalist%20style&width=600&height=400&seq=pricing-cleaning-card-v3&orientation=landscape',
                      accent: 'from-emerald-500 to-teal-500',
                      accentLight: 'bg-emerald-50',
                      iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-500',
                      borderColor: 'border-emerald-200',
                      tagColor: 'bg-emerald-100 text-emerald-700',
                      tag: 'Best Value'
                    },
                    {
                      key: 'waste',
                      icon: 'ri-delete-bin-fill',
                      title: 'Waste Removal',
                      subtitle: 'All Waste Types',
                      description: 'Furniture, garden waste, or full house clearances.',
                      bullets: ['Furniture removal', 'Garden waste', 'House clearance', 'Single item pickup'],
                      priceMain: 'From £90',
                      priceSuffix: '/load',
                      priceNote: 'Per van load',
                      image: 'https://readdy.ai/api/search-image?query=professional%20waste%20removal%20team%20loading%20old%20furniture%20into%20clean%20white%20van%20outside%20modern%20home%20on%20sunny%20day%20warm%20natural%20lighting%20clean%20simple%20background%20minimalist%20style&width=600&height=400&seq=pricing-waste-card-v3&orientation=landscape',
                      accent: 'from-rose-500 to-pink-500',
                      accentLight: 'bg-rose-50',
                      iconBg: 'bg-gradient-to-br from-rose-500 to-pink-500',
                      borderColor: 'border-rose-200',
                      tagColor: 'bg-rose-100 text-rose-700',
                      tag: 'Same Day'
                    },
                    {
                      key: 'transport',
                      icon: 'ri-truck-fill',
                      title: 'Transport',
                      subtitle: 'Man & Van Services',
                      description: 'Reliable man & van for small moves and furniture delivery.',
                      bullets: ['Man & van hire', 'Furniture delivery', 'Small house moves', 'Storage runs'],
                      priceMain: '£1.50',
                      priceSuffix: '/mile',
                      priceNote: 'Min charge applies',
                      image: 'https://readdy.ai/api/search-image?query=professional%20mover%20carrying%20cardboard%20boxes%20next%20to%20clean%20white%20delivery%20van%20outside%20modern%20home%20on%20sunny%20day%20warm%20natural%20lighting%20clean%20simple%20background%20minimalist%20style&width=600&height=400&seq=pricing-transport-card-v3&orientation=landscape',
                      accent: 'from-slate-600 to-slate-800',
                      accentLight: 'bg-slate-50',
                      iconBg: 'bg-gradient-to-br from-slate-600 to-slate-800',
                      borderColor: 'border-slate-200',
                      tagColor: 'bg-slate-100 text-slate-700',
                      tag: 'Flexible'
                    }
                  ].map((item, index) => (
                    <div 
                      key={index}
                      onClick={scrollToTop}
                      className={`flex-shrink-0 w-[260px] bg-white rounded-2xl border ${item.borderColor} overflow-hidden shadow-lg cursor-pointer active:scale-[0.98] transition-all duration-200`}
                    >
                      {/* Image Section */}
                      <div className="relative w-full h-32 overflow-hidden">
                        <img
                          src={item.image}
                          alt={`${item.title} service`}
                          className="w-full h-full object-cover object-top"
                        />
                        {/* Gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>
                        
                        {/* Tag badge */}
                        <div className="absolute top-2 left-2">
                          <span className={`${item.tagColor} text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm shadow-sm`}>
                            {item.tag}
                          </span>
                        </div>
                        
                        {/* Price overlay */}
                        <div className="absolute bottom-2 right-2 bg-white/95 backdrop-blur-sm rounded-lg px-2 py-1 shadow-md">
                          <div className="flex items-baseline gap-0.5">
                            <span className={`text-sm font-black bg-gradient-to-r ${item.accent} bg-clip-text text-transparent`}>
                              {item.priceMain}
                            </span>
                            <span className="text-[9px] text-gray-500 font-medium">{item.priceSuffix}</span>
                          </div>
                          <p className="text-[8px] text-gray-400 font-medium">{item.priceNote}</p>
                        </div>
                        
                        {/* Icon badge */}
                        <div className="absolute bottom-2 left-2">
                          <div className={`${item.iconBg} w-8 h-8 rounded-lg flex items-center justify-center shadow-md`}>
                            <i className={`${item.icon} text-sm text-white`}></i>
                          </div>
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="p-3">
                        {/* Title & Subtitle */}
                        <div className="mb-2">
                          <h3 className="text-sm font-bold text-gray-900 leading-tight">{item.title}</h3>
                          <p className="text-[10px] text-gray-500 font-medium">{item.subtitle}</p>
                        </div>
                        
                        {/* Description */}
                        <p className="text-[11px] text-gray-600 leading-relaxed mb-2 line-clamp-2">
                          {item.description}
                        </p>
                        
                        {/* Bullet list - show only 2 on mobile */}
                        <ul className="space-y-1 mb-3">
                          {item.bullets.slice(0, 2).map((b, i) => (
                            <li key={i} className="flex items-center gap-1.5">
                              <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${item.accent} flex items-center justify-center flex-shrink-0`}>
                                <i className="ri-check-line text-[6px] text-white font-bold"></i>
                              </div>
                              <span className="text-[11px] text-gray-700 leading-tight">{b}</span>
                            </li>
                          ))}
                        </ul>
                        
                        {/* Book button */}
                        <button className={`w-full py-2 rounded-lg bg-gradient-to-r ${item.accent} text-white text-xs font-bold shadow-md whitespace-nowrap cursor-pointer flex items-center justify-center gap-1`}>
                          Book Now
                          <i className="ri-arrow-right-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* After hours notice */}
              <div className="mt-4 bg-gradient-to-r from-[#22304e] to-[#2d4263] rounded-xl p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-md">
                  <i className="ri-time-line text-white text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white">After-Hours Pricing</p>
                  <p className="text-[10px] text-white/70">
                    <span className="text-amber-400 font-semibold">6pm–6am:</span> +20% &nbsp;•&nbsp;
                    <span className="text-amber-400 font-semibold">After 8pm:</span> +£50 callout
                  </p>
                </div>
              </div>

              <Link
                to="/pricing"
                className="mt-4 w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-orange-500/25 cursor-pointer active:scale-[0.98] transition-transform"
              >
                <i className="ri-price-tag-3-fill"></i>
                View Full Pricing
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          {/* Areas We Cover Section - Mobile */}
          <div className="py-10 px-4 bg-gradient-to-br from-slate-50 via-white to-orange-50/30 relative overflow-hidden">
            {/* Decorative background */}
            <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-gradient-to-br from-orange-100/50 via-amber-500/30 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-gradient-to-tr from-teal-100/40 via-emerald-50/20 to-transparent rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-5">
                <div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-200/60 rounded-full mb-2">
                    <i className="ri-map-pin-fill text-xs"></i>
                    <span className="text-xs font-bold">12 UK CITIES</span>
                  </div>
                  <h2 className="text-xl font-bold text-[#0F2A44]">Areas We Cover</h2>
                </div>
                <Link to="/locations" className="text-[#FF6A00] text-sm font-semibold flex items-center gap-1">
                  View All
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>

              {/* Featured Cities - Horizontal Scroll */}
              <div className="overflow-x-auto hide-scrollbar -mx-4 px-4 mb-4">
                <div className="flex gap-3 pb-2">
                  {[
                    { city: 'Birmingham', region: 'West Midlands', link: '/location-birmingham', desc: 'Trusted local professionals for all home services including repairs, maintenance, cleaning, and transport.', accent: 'from-orange-500 to-amber-500', accentLight: 'from-orange-50 to-amber-50', iconBg: 'bg-gradient-to-br from-orange-500 to-amber-500' },
                    { city: 'Glasgow', region: 'Scotland', link: '/location-glasgow', desc: 'ID-verified tradespeople ready to help with property maintenance, repairs, and emergency services.', accent: 'from-sky-500 to-cyan-500', accentLight: 'from-sky-50 to-cyan-50', iconBg: 'bg-gradient-to-br from-sky-500 to-cyan-500' },
                    { city: 'Leeds', region: 'Yorkshire', link: '/location-leeds', desc: 'Same-day and scheduled services from vetted professionals across all trades and home services.', accent: 'from-amber-500 to-yellow-500', accentLight: 'from-amber-50 to-yellow-50', iconBg: 'bg-gradient-to-br from-amber-500 to-yellow-500' },
                    { city: 'Liverpool', region: 'Merseyside', link: '/location-liverpool', desc: 'Quality home services from local professionals for homeowners, landlords, and property managers.', accent: 'from-red-500 to-rose-500', accentLight: 'from-red-50 to-rose-50', iconBg: 'bg-gradient-to-br from-red-500 to-rose-500' },
                    { city: 'London', region: 'Greater London', link: '/location-london', desc: 'Professional services across all London boroughs with verified tradespeople available 24/7.', accent: 'from-violet-500 to-purple-500', accentLight: 'from-violet-50 to-purple-50', iconBg: 'bg-gradient-to-br from-violet-500 to-purple-500' },
                    { city: 'Manchester', region: 'Greater Manchester', link: '/location-manchester', desc: 'Reliable home services from trusted local professionals for urgent and planned work.', accent: 'from-teal-500 to-emerald-500', accentLight: 'from-teal-50 to-emerald-50', iconBg: 'bg-gradient-to-br from-teal-500 to-emerald-500' },
                    { city: 'Bristol', region: 'South West England', link: '/location-bristol', desc: 'Verified professionals delivering quality repairs, maintenance, cleaning, and transport services.', accent: 'from-emerald-500 to-green-500', accentLight: 'from-emerald-50 to-green-50', iconBg: 'bg-gradient-to-br from-emerald-500 to-green-500' },
                    { city: 'Cardiff', region: 'Wales', link: '/location-cardiff', desc: 'Trusted tradespeople for all home services including plumbing, electrical, handyman, and more.', accent: 'from-rose-500 to-pink-500', accentLight: 'from-rose-50 to-pink-50', iconBg: 'bg-gradient-to-br from-rose-500 to-pink-500' },
                    { city: 'Edinburgh', region: 'Scotland', link: '/location-edinburgh', desc: 'Professional home services from local experts for property maintenance and emergency repairs.', accent: 'from-indigo-500 to-blue-500', accentLight: 'from-indigo-50 to-blue-50', iconBg: 'bg-gradient-to-br from-indigo-500 to-blue-500' },
                    { city: 'Newcastle', region: 'North East England', link: '/location-newcastle', desc: 'Quality services from verified professionals for all your home maintenance and repair needs.', accent: 'from-slate-600 to-slate-800', accentLight: 'from-slate-50 to-gray-100', iconBg: 'bg-gradient-to-br from-slate-600 to-slate-800' },
                    { city: 'Newport', region: 'Wales', link: '/location-newport', desc: 'Local professionals ready to help with repairs, maintenance, cleaning, and transport services.', accent: 'from-cyan-500 to-teal-500', accentLight: 'from-cyan-50 to-teal-50', iconBg: 'bg-gradient-to-br from-cyan-500 to-teal-500' },
                    { city: 'Swansea', region: 'Wales', link: '/location-swansea', desc: 'Trusted home services from verified tradespeople for homeowners and property managers.', accent: 'from-fuchsia-500 to-pink-500', accentLight: 'from-fuchsia-50 to-pink-50', iconBg: 'bg-gradient-to-br from-fuchsia-500 to-pink-500' }
                  ].map((area, index) => (
                    <Link
                      key={index}
                      to={area.link}
                      className="group relative flex flex-col bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.12)] cursor-pointer min-w-[280px]"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {/* Top gradient accent bar */}
                      <div
                        className={`h-1 w-full bg-gradient-to-r ${area.accent} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}
                      ></div>

                      <div className="p-5 flex flex-col h-full">
                        {/* Icon + Title row */}
                        <div className="flex items-start gap-3 mb-4">
                          <div
                            className={`${area.iconBg} w-11 h-11 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 flex-shrink-0`}
                          >
                            <i className="ri-map-pin-2-fill text-lg text-white"></i>
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-base font-bold text-gray-900 leading-tight group-hover:text-[#ff751f] transition-colors duration-300">
                              {area.city}
                            </h3>
                            <p className={`text-xs font-semibold bg-gradient-to-r ${area.accent} bg-clip-text text-transparent mt-0.5`}>
                              {area.region}
                            </p>
                          </div>
                        </div>

                        {/* Divider */}
                        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-4"></div>

                        {/* Description */}
                        <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow">
                          {area.desc}
                        </p>

                        {/* CTA row */}
                        <div className="flex items-center justify-between mt-auto">
                          <span className="text-xs font-semibold text-gray-400 group-hover:text-[#ff751f] transition-colors duration-300">
                            View Services
                          </span>
                          <div
                            className={`w-7 h-7 rounded-full bg-gradient-to-br ${area.accent} flex items-center justify-center opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-400 shadow-md`}
                          >
                            <i className="ri-arrow-right-line text-white text-xs"></i>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* All Cities Grid */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
                {/* Grid header */}
                <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                      <i className="ri-global-line text-white text-xs"></i>
                    </div>
                    <span className="text-sm font-bold text-[#0F2A44]">All Service Areas</span>
                  </div>
                </div>
                
                {/* Cities grid */}
                <div className="grid grid-cols-2 divide-x divide-y divide-gray-100">
                  {[
                    { city: 'Birmingham', region: 'West Midlands', link: '/location-birmingham', accent: 'from-orange-500 to-amber-500' },
                    { city: 'Glasgow', region: 'Scotland', link: '/location-glasgow', accent: 'from-sky-500 to-cyan-500' },
                    { city: 'Leeds', region: 'Yorkshire', link: '/location-leeds', accent: 'from-amber-500 to-yellow-500' },
                    { city: 'Liverpool', region: 'Merseyside', link: '/location-liverpool', accent: 'from-red-500 to-rose-500' },
                    { city: 'London', region: 'Greater London', link: '/location-london', accent: 'from-orange-500 to-amber-500' },
                    { city: 'Manchester', region: 'Greater Manchester', link: '/location-manchester', accent: 'from-teal-500 to-emerald-500' },
                    { city: 'Bristol', region: 'South West', link: '/location-bristol', accent: 'from-emerald-500 to-green-500' },
                    { city: 'Cardiff', region: 'Wales', link: '/location-cardiff', accent: 'from-rose-500 to-pink-500' },
                    { city: 'Edinburgh', region: 'Scotland', link: '/location-edinburgh', accent: 'from-indigo-500 to-blue-500' },
                    { city: 'Newcastle', region: 'North East', link: '/location-newcastle', accent: 'from-slate-600 to-slate-800' },
                    { city: 'Newport', region: 'Wales', link: '/location-newport', accent: 'from-cyan-500 to-teal-500' },
                    { city: 'Swansea', region: 'Wales', link: '/location-swansea', accent: 'from-fuchsia-500 to-pink-500' }
                  ].map((area, index) => (
                    <Link
                      key={index}
                      to={area.link}
                      className="group flex items-center gap-2.5 p-3 hover:bg-gradient-to-r hover:from-orange-50/50 hover:to-transparent transition-all cursor-pointer"
                    >
                      <div className={`w-8 h-8 flex-shrink-0 bg-gradient-to-br ${area.accent} rounded-lg flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform`}>
                        <i className="ri-map-pin-2-fill text-sm text-white"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-[#0F2A44] leading-tight group-hover:text-[#ff751f] transition-colors duration-300 truncate">
                          {area.city}
                        </h3>
                        <p className="text-[10px] text-gray-500 font-medium truncate">
                          {area.region}
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#ff751f] transition-colors flex-shrink-0"></i>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Bottom CTA */}
              <div className="mt-4 bg-gradient-to-r from-[#22304e] to-[#2d4263] rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
                  <i className="ri-customer-service-2-fill text-white text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white">Not in your area?</p>
                  <p className="text-[11px] text-white/70">We're expanding! Join our waitlist.</p>
                </div>
                <Link
                  to="/locations"
                  className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>

          {/* Trust Section - MOBILE */}
          <div className="py-10 px-4 bg-white relative overflow-hidden">
            {/* Decorative background */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-orange-50 to-transparent rounded-full"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-teal-50 to-transparent rounded-full"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-200/60 rounded-full mb-3">
                  <i className="ri-shield-check-fill text-teal-600 text-sm"></i>
                  <span className="text-xs font-semibold text-teal-700 uppercase">Trust & Safety</span>
                </div>
                <h2 className="text-xl font-bold text-[#0F2A44]">
                  Built for Peace of Mind
                </h2>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-shield-check-fill', text: 'Verified Providers', desc: 'ID-verified professionals', color: 'from-orange-500 to-amber-500' },
                  { icon: 'ri-lock-fill', text: 'Secure Payments', desc: 'Protected transactions', color: 'from-teal-500 to-emerald-500' },
                  { icon: 'ri-star-fill', text: 'Rated & Reviewed', desc: 'Real customer feedback', color: 'from-amber-500 to-yellow-500' },
                  { icon: 'ri-customer-service-2-fill', text: '24/7 Support', desc: 'Always here to help', color: 'from-rose-500 to-pink-500' }
                ].map((feature, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100 shadow-sm text-center">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center mx-auto mb-3 shadow-md`}>
                      <i className={`${feature.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-sm font-bold text-[#0F2A44] mb-1">
                      {feature.text}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {feature.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ Section - MOBILE */}
          <div className="py-10 px-4 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-24 h-24 bg-orange-100 rounded-full filter blur-2xl opacity-30"></div>
            <div className="absolute bottom-0 right-0 w-24 h-24 bg-teal-100 rounded-full filter blur-2xl opacity-30"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-200/60 rounded-full mb-3">
                  <i className="ri-question-fill text-[#ff751f] text-sm"></i>
                  <span className="text-xs font-semibold text-[#ff751f] uppercase">FAQ</span>
                </div>
                <h2 className="text-xl font-bold text-[#0F2A44]">
                  Frequently Asked Questions
                </h2>
              </div>
              
              <div className="space-y-3">
                {[
                  {
                    question: 'How quickly can I get a provider?',
                    answer: 'Availability depends on your location and the service requested. Many jobs are accepted quickly by nearby providers.'
                  },
                  {
                    question: 'Are providers verified?',
                    answer: 'Yes. All providers are ID-verified, DBS checked where applicable, and fully insured.'
                  },
                  {
                    question: 'Do I pay upfront?',
                    answer: 'You pay securely in the app. Payment is only taken once the job is completed.'
                  },
                  {
                    question: 'Can I cancel a booking?',
                    answer: 'Yes. Cancellations are subject to our Cancellation & Refund Policy, depending on timing.'
                  },
                  {
                    question: 'Are prices fixed?',
                    answer: 'You\'ll see pricing clearly before booking. There are no hidden fees.'
                  }
                ].map((faq, index) => (
                  <details key={index} className="group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <summary className="px-4 py-3 flex items-center justify-between cursor-pointer font-semibold text-[#0F2A44] text-sm hover:bg-orange-50 transition-colors">
                      <span className="pr-4">{faq.question}</span>
                      <i className="ri-arrow-down-s-line text-xl text-[#FF6A00] flex-shrink-0 group-open:rotate-180 transition-transform duration-300"></i>
                    </summary>
                    <div className="px-4 pb-4 text-gray-600 text-sm border-t border-gray-100 pt-3">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>

          {/* Final CTA Section - MOBILE */}
          <div className="mx-4 my-10 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-500/20 to-transparent rounded-full blur-xl"></div>
            <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-[#ff751f]/20 to-transparent rounded-full blur-xl"></div>
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ff751f] via-orange-400 to-amber-400"></div>
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1.5 mb-4">
                <div className="w-2 h-2 bg-[#ff751f] rounded-full animate-pulse"></div>
                <span className="text-white text-xs font-medium">Ready to Get Started?</span>
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-3">
                Need a job done today?
              </h2>
              <p className="text-white/80 text-sm mb-6">
                Connect with verified local professionals in minutes.
              </p>
              
              <button
                onClick={scrollToTop}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-[#ff751f] to-orange-500 text-white font-bold text-sm shadow-lg shadow-orange-500/40 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 hover:from-orange-500 hover:to-orange-400 transition-all"
              >
                Get matched now
                <i className="ri-arrow-right-line"></i>
              </button>
              
              <div className="mt-4 flex items-center justify-center gap-4 text-white/70 text-sm">
                <div className="flex items-center gap-1">
                  <i className="ri-shield-check-line text-emerald-400"></i>
                  <span>Verified</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line text-[#ff751f]"></i>
                  <span>Same Day</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-star-line text-amber-400"></i>
                  <span>Top Rated</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6">
            <Footer />
          </div>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block">
        {/* 2. Introduction - Why TaskBudy (MOVED HERE) */}
        <section className="py-24 bg-gradient-to-br from-slate-50 via-white to-orange-50/30 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-orange-100/40 via-amber-50/20 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-teal-100/30 via-emerald-50/10 to-transparent rounded-full blur-3xl"></div>
            <div
              className="absolute inset-0 opacity-[0.02]"
              style={{
                backgroundImage: `radial-gradient(circle, #000 1px, transparent 1px)`,
                backgroundSize: '32px 32px',
              }}
            ></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-200/60 text-orange-600 px-5 py-2.5 rounded-full text-sm font-semibold mb-6">
                <i className="ri-sparkling-fill"></i>
                <span>Why TaskBudy</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#22304e] mb-6 leading-tight">
                Home services, simplified
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                  One platform, every trade
                </span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                TaskBudy connects homeowners, landlords, and property managers with verified local professionals — making it easy to book trusted tradespeople for any job, big or small.
              </p>
            </div>

            {/* Introduction Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
              {/* Card 1 - Verified Professionals */}
              <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <i className="ri-shield-user-fill text-2xl text-white"></i>
                  </div>
                </div>
                <div className="pt-8 text-center">
                  <h3 className="text-xl font-bold text-[#22304e] mb-3">Verified & Vetted</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Every provider is ID-verified, background-checked, and fully insured before they join the platform — so you can book with complete confidence.
                  </p>
                  <div className="inline-flex items-center gap-2 text-orange-600 font-semibold text-sm">
                    <i className="ri-check-line"></i>
                    <span>DBS Checked & Insured</span>
                  </div>
                </div>
              </div>

              {/* Card 2 - Transparent Pricing */}
              <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <i className="ri-price-tag-3-fill text-2xl text-white"></i>
                  </div>
                </div>
                <div className="pt-8 text-center">
                  <h3 className="text-xl font-bold text-[#22304e] mb-3">Upfront Pricing</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    No quotes, no negotiations, no hidden fees. See clear pricing before you book — whether it&apos;s by time, load size, or distance.
                  </p>
                  <div className="inline-flex items-center gap-2 text-teal-600 font-semibold text-sm">
                    <i className="ri-check-line"></i>
                    <span>No Hidden Fees, Ever</span>
                  </div>
                </div>
              </div>

              {/* Card 3 - Fast & Reliable */}
              <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <i className="ri-flashlight-fill text-2xl text-white"></i>
                  </div>
                </div>
                <div className="pt-8 text-center">
                  <h3 className="text-xl font-bold text-[#22304e] mb-3">Fast & Reliable</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Request a service and get matched with a nearby professional quickly. Same-day availability across all major UK cities.
                  </p>
                  <div className="inline-flex items-center gap-2 text-rose-600 font-semibold text-sm">
                    <i className="ri-check-line"></i>
                    <span>Same-Day Service Available</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Strip */}
            <div className="bg-gradient-to-r from-[#22304e] to-[#2d4263] rounded-2xl p-8 md:p-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-map-pin-fill text-xl text-orange-400"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-1">12+ Cities</h4>
                  <p className="text-white/70 text-sm">Across the UK</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-tools-fill text-xl text-teal-400"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-1">6 Trade Categories</h4>
                  <p className="text-white/70 text-sm">Every home service covered</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-shield-check-fill text-xl text-emerald-400"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-1">100% Verified</h4>
                  <p className="text-white/70 text-sm">Every provider checked</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-customer-service-2-fill text-xl text-rose-400"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-1">24/7 Support</h4>
                  <p className="text-white/70 text-sm">Always here to help</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* 3. How It Works */}
        <HowItWorks />
        
        {/* 4. Pricing Cards - MOVED HERE */}
        <PricingSection />
        
        {/* 5. Areas We Cover */}
        <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 via-white to-orange-50/30 overflow-hidden" aria-labelledby="tb-areas-title">
          {/* Decorative background */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-orange-100/50 via-amber-500/30 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-gradient-to-tr from-teal-100/40 via-emerald-50/20 to-transparent rounded-full blur-3xl"></div>
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `radial-gradient(circle, #000 1px, transparent 1px)`,
                backgroundSize: '32px 32px',
              }}
            ></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <header className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-200/60 rounded-full mb-2">
                <i className="ri-map-pin-fill text-xs"></i>
                <span className="text-xs font-bold">12 UK CITIES</span>
              </div>
              <h2 id="tb-areas-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Areas We Cover
              </h2>
              <p className="text-lg text-gray-500 max-w-3xl mx-auto">
                Task-Budy provides reliable handyman, plumbing, electrical, cleaning, waste removal, and transport services across major cities in the UK.
              </p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { city: 'Birmingham', region: 'West Midlands', link: '/location-birmingham', desc: 'Quality home services in Birmingham. Verified local professionals for handyman, plumbing, electrical, cleaning, waste removal, and transport.', accent: 'from-orange-500 to-amber-500', accentLight: 'from-orange-50 to-amber-50', iconBg: 'bg-gradient-to-br from-orange-500 to-amber-500' },
                { city: 'Glasgow', region: 'Scotland', link: '/location-glasgow', desc: 'Professional home services in Glasgow. ID-verified tradespeople for property maintenance, repairs, cleaning, waste removal, and transport.', accent: 'from-sky-500 to-cyan-500', accentLight: 'from-sky-50 to-cyan-50', iconBg: 'bg-gradient-to-br from-sky-500 to-cyan-500' },
                { city: 'Leeds', region: 'Yorkshire', link: '/location-leeds', desc: 'Reliable home services in Leeds. Vetted professionals for urgent and scheduled work across handyman, plumbing, electrical, cleaning, waste removal, and transport.', accent: 'from-amber-500 to-yellow-500', accentLight: 'from-amber-50 to-yellow-50', iconBg: 'bg-gradient-to-br from-amber-500 to-yellow-500' },
                { city: 'Liverpool', region: 'Merseyside', link: '/location-liverpool', desc: 'Trusted home services in Liverpool. Local professionals delivering plumbing, electrical, cleaning, handyman, waste removal, and transport services.', accent: 'from-red-500 to-rose-500', accentLight: 'from-red-50 to-rose-50', iconBg: 'bg-gradient-to-br from-red-500 to-rose-500' },
                { city: 'London', region: 'Greater London', link: '/location-london', desc: 'Professional home services across London. Verified local tradespeople for same-day and scheduled bookings in handyman, plumbing, electrical, cleaning, waste removal, and transport.', accent: 'from-violet-500 to-purple-500', accentLight: 'from-violet-50 to-purple-50', iconBg: 'bg-gradient-to-br from-violet-500 to-purple-500' },
                { city: 'Manchester', region: 'Greater Manchester', link: '/location-manchester', desc: 'Trusted home services in Manchester. Local professionals supporting homeowners, landlords, and property managers with repairs, cleaning, waste removal, and transport.', accent: 'from-teal-500 to-emerald-500', accentLight: 'from-teal-50 to-emerald-50', iconBg: 'bg-gradient-to-br from-teal-500 to-emerald-500' },
                { city: 'Bristol', region: 'South West England', link: '/location-bristol', desc: 'Quality home services in Bristol. Verified professionals ready to help with property maintenance, repairs, cleaning, waste removal, and transport.', accent: 'from-emerald-500 to-green-500', accentLight: 'from-emerald-50 to-green-50', iconBg: 'bg-gradient-to-br from-emerald-500 to-green-500' },
                { city: 'Cardiff', region: 'Wales', link: '/location-cardiff', desc: 'Professional home services in Cardiff. Trusted local tradespeople for handyman services, plumbing, electrical work, cleaning, waste removal, and transport.', accent: 'from-rose-500 to-pink-500', accentLight: 'from-rose-50 to-pink-50', iconBg: 'bg-gradient-to-br from-rose-500 to-pink-500' },
                { city: 'Edinburgh', region: 'Scotland', link: '/location-edinburgh', desc: 'Professional home services in Edinburgh. Trusted local tradespeople for handyman services, plumbing, electrical work, cleaning, waste removal, and transport.', accent: 'from-indigo-500 to-blue-500', accentLight: 'from-indigo-50 to-blue-50', iconBg: 'bg-gradient-to-br from-indigo-500 to-blue-500' },
                { city: 'Newcastle', region: 'North East England', link: '/location-newcastle', desc: 'Quality home services in Newcastle. Verified professionals ready to help with property maintenance, repairs, cleaning, waste removal, and transport.', accent: 'from-slate-600 to-slate-800', accentLight: 'from-slate-50 to-gray-100', iconBg: 'bg-gradient-to-br from-slate-600 to-slate-800' },
                { city: 'Newport', region: 'Wales', link: '/location-newport', desc: 'Professional home services in Newport. Trusted local tradespeople for handyman services, plumbing, electrical work, cleaning, waste removal, and transport.', accent: 'from-cyan-500 to-teal-500', accentLight: 'from-cyan-50 to-teal-50', iconBg: 'bg-gradient-to-br from-cyan-500 to-teal-500' },
                { city: 'Swansea', region: 'Wales', link: '/location-swansea', desc: 'Quality home services in Swansea. Verified professionals for handyman services, plumbing, electrical work, cleaning, waste removal, and transport.', accent: 'from-fuchsia-500 to-pink-500', accentLight: 'from-fuchsia-50 to-pink-50', iconBg: 'bg-gradient-to-br from-fuchsia-500 to-pink-500' }
              ].map((area, index) => (
                <Link
                  key={index}
                  to={area.link}
                  className="group relative flex flex-col bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.12)] cursor-pointer h-full"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Top gradient accent bar */}
                  <div
                    className={`h-1 w-full bg-gradient-to-r ${area.accent} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}
                  ></div>

                  <div className="p-5 flex flex-col flex-grow">
                    {/* Icon + Title row */}
                    <div className="flex items-start gap-3 mb-4">
                      <div
                        className={`${area.iconBg} w-11 h-11 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 flex-shrink-0`}
                      >
                        <i className="ri-map-pin-2-fill text-lg text-white"></i>
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-bold text-gray-900 leading-tight group-hover:text-[#ff751f] transition-colors duration-300">
                          {area.city}
                        </h3>
                        <p className={`text-xs font-semibold bg-gradient-to-r ${area.accent} bg-clip-text text-transparent mt-0.5`}>
                          {area.region}
                        </p>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-4"></div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4 flex-grow">
                      {area.desc}
                    </p>

                    {/* CTA row */}
                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-xs font-semibold text-gray-400 group-hover:text-[#ff751f] transition-colors duration-300">
                        View Services
                      </span>
                      <div
                        className={`w-7 h-7 rounded-full bg-gradient-to-br ${area.accent} flex items-center justify-center opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-400 shadow-md`}
                      >
                        <i className="ri-arrow-right-line text-white text-xs"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 7. Trust & Safety */}
        <TrustSafety />
        
        {/* 8. FAQ */}
        <FAQSection />
        
        {/* 9. Final CTA */}
        <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-500/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#ff751f]/15 to-transparent rounded-full blur-2xl"></div>
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ff751f] via-orange-400 to-amber-400"></div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <div className="w-2 h-2 bg-[#ff751f] rounded-full animate-pulse"></div>
              <span className="text-white text-sm font-medium">Ready to Get Started?</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Need a job done today?
            </h2>
            
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Connect with verified local professionals in minutes. Fast, reliable, and transparent.
            </p>
            
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-gradient-to-r from-[#ff751f] to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white px-10 py-4 rounded-xl text-lg font-bold transition-all shadow-lg shadow-orange-500/40 hover:shadow-xl hover:shadow-orange-500/50 hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              Get matched with a local provider
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
            
            <div className="mt-8 flex items-center justify-center gap-8 text-white/70 text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-emerald-400 text-lg"></i>
                <span>Verified Professionals</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-[#ff751f] text-lg"></i>
                <span>Same Day Service</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-star-line text-amber-400 text-lg"></i>
                <span>Top Rated</span>
              </div>
            </div>
          </div>
        </section>

        {/* 10. Footer */}
        <Footer />
        
        {selectedService && (
          <ServiceModal 
            service={selectedService} 
            onClose={handleCloseModal}
          />
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100 animate-slideDown">
            <div className="px-6 py-4 space-y-1">
              <a
                href="/services"
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 rounded-lg transition-all duration-300 hover:translate-x-2 group"
              >
                <i className="ri-tools-line text-xl group-hover:scale-110 transition-transform duration-300"></i>
                <span>Services</span>
              </a>
              <a
                href="/locations"
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 rounded-lg transition-all duration-300 hover:translate-x-2 group"
              >
                <i className="ri-map-pin-line text-xl group-hover:scale-110 transition-transform duration-300"></i>
                <span>Locations</span>
              </a>
              <a
                href="/pricing"
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 rounded-lg transition-all duration-300 hover:translate-x-2 group"
              >
                <i className="ri-price-tag-3-line text-xl group-hover:scale-110 transition-transform duration-300"></i>
                <span>Pricing</span>
              </a>
              <a
                href="/about"
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 rounded-lg transition-all duration-300 hover:translate-x-2 group"
              >
                <i className="ri-information-line text-xl group-hover:scale-110 transition-transform duration-300"></i>
                <span>About</span>
              </a>
              <a
                href="/contact"
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 rounded-lg transition-all duration-300 hover:translate-x-2 group"
              >
                <i className="ri-customer-service-2-line text-xl group-hover:scale-110 transition-transform duration-300"></i>
                <span>Contact</span>
              </a>
              <div className="pt-3 border-t border-gray-100">
                <a
                  href="/provider-signup"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 whitespace-nowrap group"
                >
                  <i className="ri-user-add-line text-lg group-hover:rotate-12 transition-transform duration-300"></i>
                  <span>Become a Provider</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
